/**
 * Wire protocol adapter.
 *
 * Backend emits NDJSON events with `type` values like
 * `outputText`, `suggestedActions`, `productList`, etc.
 *
 * This module translates those backend events into the SDK's normalized
 * `StreamEvent` model.
 *
 * Also handles JSON-mode responses from `similar_products` and
 * `product_groupings` endpoints.
 */

import type {
  StreamEvent,
  StreamEventMetadata,
  StreamEventTextChunk,
  StreamEventUISpec,
  StreamEventAction,
  StreamEventDone,
  StreamEventError,
  UIElement,
} from './types.js';
import { getSuggestedSearchKeywordsText } from './suggested-search-keywords.js';
import { isConsultingSource } from './consulting-sources.js';
import { firstNonEmptyStringValue, nonEmptyStringList, normalizeFindSimilarPayload } from './find-similar-payload.js';

type WidgetName = 'chat' | 'qna' | 'simrel';

interface V1RequestDetails {
  type?: string;
  payload?: unknown;
  [key: string]: unknown;
}

interface V1ProductSuggestionsLabel {
  label?: string;
  sentiment?: string;
  [key: string]: unknown;
}

interface V1ReviewHighlightItem {
  review_class?: string;
  review_text?: string;
  review_rating?: string | number;
  review_tag?: string;
  [key: string]: unknown;
}

interface V1OutputText {
  type: 'outputText';
  payload: {
    text?: string;
    plain_text?: string;
    is_error?: boolean;
    [key: string]: unknown;
  };
}

interface V1SuggestedActionItem {
  title?: string;
  icon?: string;
  image?: string | null;
  requestDetails?: V1RequestDetails;
  [key: string]: unknown;
}

interface V1SuggestedActions {
  type: 'suggestedActions';
  payload: {
    actions?: V1SuggestedActionItem[];
    [key: string]: unknown;
  };
}

export interface V1Product {
  sku: string;
  name: string;
  brand?: string;
  images?: string[];
  price?: number;
  price_discounted?: number;
  price_discount_rate?: number;
  price_currency?: string;
  discount_reason?: string;
  url?: string;
  rating?: number;
  review_count?: number;
  cart_code?: string;
  in_stock?: boolean;
  description?: string;
  description_html?: string;
  features?: Array<{ name?: string; key?: string; value?: string | number | boolean; [key: string]: unknown }>;
  specifications?: Record<string, string> | Array<{ key: string; value: string }>;
  short_name?: string;
  category_ids?: string[];
  category_names?: string[];
  variants?: Array<Record<string, unknown>>;
  facet_hits?: Record<string, unknown> | null;
  promotions?: string[];
}

interface V1ProductList {
  type: 'productList';
  payload: {
    product_list?: V1Product[];
    grouped_assignment?: Array<{
      title?: string;
      skus?: string[];
      [key: string]: unknown;
    }>;
    source?: string;
    title?: string;
    offset?: number;
    page_size?: number;
    end_of_list?: boolean;
    // search-revision additions: multi-emission product_list support.
    // `ranking_state='pending'` → render blurred/shimmer + replace semantics
    // `ranking_state='final'` → swap in unblurred + replace same sequence
    // A missing ranking_state keeps the pre-search-revision behavior (single
    // emission, no blur).
    ranking_state?: 'pending' | 'final';
    sequence_id?: string;
    recommendation_groups?: Array<{
      label?: string;
      reason?: string;
      skus?: string[];
      [key: string]: unknown;
    }>;
    style_variations?: Array<{
      style_label?: string;
      style_mood?: string;
      image_url?: string | null;
      recommendation_groups?: Array<{
        label?: string;
        reason?: string;
        skus?: string[];
        [key: string]: unknown;
      }>;
      product_list?: V1Product[];
      [key: string]: unknown;
    }>;
    [key: string]: unknown;
  };
}

interface V1ProductDetails {
  type: 'productDetails';
  payload: { productDetails?: V1Product; [key: string]: unknown };
}

interface V1ProductDetailsSimilars {
  type: 'productDetailsSimilars';
  payload: { similarProducts?: V1Product[]; [key: string]: unknown };
}

interface V1ComparisonTable {
  type: 'comparisonTable';
  payload: {
    multiple_product_details?: V1Product[];
    table?: Record<string, string[] | Record<string, unknown>>;
    features_list?: string[];
    product_comparison_framework?: {
      key_differences?: string[];
      recommended_choice?: string;
      recommended_choice_sku?: string;
      special_considerations?: string[];
      criteria_view?: Record<string, string>;
      criteria_view_short?: Record<string, string>;
      compared_field_names?: string[];
      winner_product?: Array<{ sku?: string; name?: string; product_detail?: { sku?: string } }>;
      winner_hits?: Record<string, { positive?: string[]; negative?: string[] }>;
      [key: string]: unknown;
    };
    [key: string]: unknown;
  };
}

interface V1Context {
  type: 'context';
  payload: {
    panel?: Record<string, unknown>;
    messages?: Array<{ role?: string; content?: string }>;
    message_id?: string;
    [key: string]: unknown;
  };
}

interface V1ChatStreamEnd {
  type: 'chatStreamEnd';
  payload?: Record<string, unknown>;
}

interface V1Loading {
  type: 'loading';
  payload: {
    text?: string;
    is_dynamic?: boolean;
    thinking_messages?: string[];
    [key: string]: unknown;
  };
}

interface V1PanelLoading {
  type: 'panelLoading';
  payload?: { text?: string; pending_type?: string; [key: string]: unknown };
}

interface V1SimilarLoading {
  type: 'similarLoading';
  payload?: { text?: string; pending_type?: string; [key: string]: unknown };
}

interface V1Redirect {
  type: 'redirect';
  payload: { url?: string; new_tab?: boolean; to?: string; [key: string]: unknown };
}

interface V1Error {
  type: 'error';
  payload?: {
    text?: string;
    message?: string;
    error?: string;
    [key: string]: unknown;
  };
}

interface V1Noop {
  type: 'dummy';
  payload?: Record<string, unknown>;
}

interface V1LauncherAction {
  type: 'launcherAction';
  payload?: Record<string, unknown>;
}

interface V1LauncherText {
  type: 'text';
  payload: {
    type?: string;
    text?: string;
    payload?: Record<string, unknown>;
    theme?: string;
    [key: string]: unknown;
  };
}

interface V1ProductItem {
  type: 'productItem';
  payload: V1Product & { group_id?: string; [key: string]: unknown };
}

interface V1LauncherTextImage {
  type: 'text_image';
  payload: {
    type?: string;
    text?: string;
    image_url?: string;
    theme?: string;
    action?: V1RequestDetails;
    [key: string]: unknown;
  };
}

interface V1LauncherQuickQna {
  type: 'quick_qna';
  payload: {
    type?: string;
    theme?: string;
    action_list?: V1SuggestedActionItem[];
    [key: string]: unknown;
  };
}

interface V1ReviewHighlights {
  type: 'reviewHighlights';
  payload: {
    sku?: string;
    reviews?: V1ReviewHighlightItem[];
    [key: string]: unknown;
  };
}

interface V1AiProductSuggestion {
  sku?: string;
  short_name?: string;
  role?: string;
  labels?: V1ProductSuggestionsLabel[];
  reason?: string;
  expert_quality_score?: number;
  review_highlight?: string;
  /** Campaign / merchant discount label; merged into normalized product for price UI. */
  discount_reason?: string;
  product_item?: V1Product;
  requestDetails?: V1RequestDetails;
  [key: string]: unknown;
}

interface V1AiProductSuggestions {
  type: 'aiProductSuggestions';
  payload: {
    product_suggestions?: V1AiProductSuggestion[];
    [key: string]: unknown;
  };
}

interface V1AiProductGrouping {
  name?: string;
  image?: string;
  labels?: string[];
  description?: string;
  highlight?: string;
  sku?: string;
  repr_sku?: string;
  repr_image?: string;
  group_skus?: string[];
  group_products?: V1Product[];
  requestDetails?: V1RequestDetails;
  [key: string]: unknown;
}

interface V1AiProductGroupings {
  type: 'aiProductGroupings';
  payload: {
    product_groupings?: V1AiProductGrouping[];
    [key: string]: unknown;
  };
}

interface V1AiSuggestedSearch {
  short_name?: string;
  detailed_user_message?: string;
  /** Long explanatory copy — not used for the compact browse keyword line */
  why_different?: string;
  /** Preferred compact keyword chips for the browse card (see suggested-search-keywords) */
  display_keywords?: string[];
  chosen_attribute?: string;
  representative_product_sku?: string;
  group_skus?: string[];
  sku?: string;
  image?: string;
  requestDetails?: V1RequestDetails;
  [key: string]: unknown;
}

interface V1AiSuggestedSearches {
  type: 'aiSuggestedSearches';
  payload: {
    suggested_searches?: V1AiSuggestedSearch[];
    [key: string]: unknown;
  };
}

interface V1GetGroundingReview {
  type: 'getGroundingReview';
  payload: {
    title?: string;
    text?: string;
    review_count?: string;
    requestDetails?: V1RequestDetails;
    [key: string]: unknown;
  };
}

interface V1Voice {
  type: 'voice';
  payload: {
    text?: string;
    audio_base64?: string;
    content_type?: string;
    [key: string]: unknown;
  };
}

interface V1UiSpec {
  type: 'uiSpec';
  payload: {
    type: string;
    [key: string]: unknown;
  };
}

type V1StreamEvent =
  | V1OutputText
  | V1SuggestedActions
  | V1ProductList
  | V1ProductDetails
  | V1ProductDetailsSimilars
  | V1ComparisonTable
  | V1Context
  | V1ChatStreamEnd
  | V1Loading
  | V1PanelLoading
  | V1SimilarLoading
  | V1Redirect
  | V1Error
  | V1Noop
  | V1LauncherAction
  | V1LauncherText
  | V1ProductItem
  | V1LauncherTextImage
  | V1LauncherQuickQna
  | V1ReviewHighlights
  | V1AiProductSuggestions
  | V1AiProductGroupings
  | V1AiSuggestedSearches
  | V1GetGroundingReview
  | V1Voice
  | V1UiSpec
  | { type: string; payload?: unknown; [key: string]: unknown };

export function adaptBackendEvent(raw: Record<string, unknown>): StreamEvent | null {
  const type = raw['type'];
  if (typeof type !== 'string') return null;

  // Defensive: backend always sends a payload object today, but a missing/null
  // payload would crash the per-type adapters that destructure it eagerly.
  if (raw['payload'] == null) raw['payload'] = {};

  if (isNormalizedStreamEvent(raw)) {
    return raw as unknown as StreamEvent;
  }

  const event = raw as V1StreamEvent;

  switch (event.type) {
    case 'outputText':
      return adaptOutputText(event as V1OutputText);
    case 'suggestedActions':
      return adaptSuggestedActions(event as V1SuggestedActions);
    case 'productList':
      return adaptProductList(event as V1ProductList);
    case 'productDetails':
      return adaptProductDetails(event as V1ProductDetails);
    case 'productDetailsSimilars':
      return adaptProductDetailsSimilars(event as V1ProductDetailsSimilars);
    case 'comparisonTable':
      return adaptComparisonTable(event as V1ComparisonTable);
    case 'context':
      return adaptContext(event as V1Context);
    case 'chatStreamEnd':
      return adaptChatStreamEnd();
    case 'loading':
      return adaptLoading(event as V1Loading);
    case 'panelLoading':
      return adaptPanelLoading(event as V1PanelLoading);
    case 'similarLoading':
      return adaptSimilarLoading(event as V1SimilarLoading);
    case 'redirect':
      return adaptRedirect(event as V1Redirect);
    case 'error':
      return adaptV1Error(event as V1Error);
    case 'dummy':
      return adaptNoop(event as V1Noop);
    case 'launcherAction':
      return adaptLauncherAction(event as V1LauncherAction);
    case 'text':
      return adaptLauncherText(event as V1LauncherText);
    case 'productItem':
      return adaptProductItem(event as V1ProductItem);
    case 'text_image':
      return adaptLauncherTextImage(event as V1LauncherTextImage);
    case 'quick_qna':
      return adaptLauncherQuickQna(event as V1LauncherQuickQna);
    case 'reviewHighlights':
      return adaptReviewHighlights(event as V1ReviewHighlights);
    case 'aiProductSuggestions':
      return adaptAiProductSuggestions(event as V1AiProductSuggestions);
    case 'aiProductGroupings':
      return adaptAiProductGroupings(event as V1AiProductGroupings);
    case 'aiSuggestedSearches':
      return adaptAiSuggestedSearches(event as V1AiSuggestedSearches);
    case 'getGroundingReview':
      return adaptGetGroundingReview(event as V1GetGroundingReview);
    case 'voice':
      return adaptVoice(event as V1Voice);
    case 'uiSpec':
      return adaptUiSpec(event as V1UiSpec);
    default:
      if (import.meta.env?.DEV) {
        console.warn('[gengage:protocol] Unknown backend event type:', event.type);
      }
      return null;
  }
}

function isNormalizedStreamEvent(raw: Record<string, unknown>): boolean {
  const type = raw['type'];
  if (typeof type !== 'string') return false;

  switch (type) {
    case 'metadata':
      return typeof raw['sessionId'] === 'string' && typeof raw['model'] === 'string';
    case 'text_chunk':
      return typeof raw['content'] === 'string';
    case 'ui_spec': {
      const widget = raw['widget'];
      if (widget !== 'chat' && widget !== 'qna' && widget !== 'simrel') return false;
      const spec = asRecord(raw['spec']);
      if (!spec) return false;
      return typeof spec['root'] === 'string' && asRecord(spec['elements']) !== null;
    }
    case 'action': {
      const action = asRecord(raw['action']);
      return action !== null && typeof action['kind'] === 'string';
    }
    case 'error':
      return typeof raw['code'] === 'string' && typeof raw['message'] === 'string';
    case 'done':
      return true;
    default:
      return false;
  }
}

function adaptOutputText(event: V1OutputText): StreamEventTextChunk | StreamEventError {
  const renderText = firstNonEmptyString(event.payload.text, event.payload.plain_text) ?? '';
  const plainText = firstNonEmptyString(event.payload.plain_text, event.payload.text) ?? renderText;
  if (event.payload.is_error) {
    return {
      type: 'error',
      code: 'BACKEND_ERROR',
      message: plainText || 'Backend returned an error',
    };
  }
  const result: StreamEventTextChunk = {
    type: 'text_chunk',
    content: renderText,
    final: true,
  };

  const skuMap = event.payload['sku_to_product_item'];
  if (skuMap && typeof skuMap === 'object' && !Array.isArray(skuMap)) {
    result.skuToProductItem = skuMap as Record<string, Record<string, unknown>>;
  }

  // AGENTIC_ADAPTER_SEAM: legacy NDJSON mention metadata uses the same StreamEvent field as adapter events.
  const mentions = event.payload['product_mentions'];
  if (Array.isArray(mentions) && mentions.length > 0) {
    result.productMentions = mentions
      .map((m): { sku: string; short_name: string; url?: string } | null => {
        if (typeof m !== 'object' || m === null || typeof m['sku'] !== 'string') return null;
        const shortName = typeof m['short_name'] === 'string' ? m['short_name'] : '';
        if (!shortName) return null;
        const directUrl = typeof m['url'] === 'string' && m['url'].trim() ? m['url'].trim() : undefined;
        const itemUrl =
          directUrl ??
          (() => {
            const item = result.skuToProductItem?.[m['sku']];
            return typeof item?.['url'] === 'string' && item['url'].trim() ? item['url'].trim() : undefined;
          })();
        return itemUrl
          ? { sku: m['sku'], short_name: shortName, url: itemUrl }
          : { sku: m['sku'], short_name: shortName };
      })
      .filter((m): m is { sku: string; short_name: string; url?: string } => m !== null);
  }

  const convMode = event.payload['conversation_mode'];
  if (typeof convMode === 'string' && convMode) {
    result.conversationMode = convMode;
  }

  const renderHint = event.payload['render_hint'];
  if (typeof renderHint === 'string' && renderHint) {
    result.renderHint = renderHint;
  }

  if (event.payload['kvkk'] === true) {
    result.kvkk = true;
  }

  return result;
}

function adaptSuggestedActions(event: V1SuggestedActions): StreamEventUISpec {
  const entries = (event.payload.actions ?? []).map((action) => {
    if (!asRecord(action)) return null;
    const label = firstNonEmptyString(action.title) ?? '';
    const actionPayload = requestDetailsToAction(action.requestDetails, label);
    const result: ButtonEntry | null = actionPayload
      ? {
          label,
          action: actionPayload,
        }
      : null;
    if (!result) return null;
    if (typeof action.icon === 'string') result.icon = action.icon;
    if (typeof action.image === 'string') result.image = action.image;
    return result;
  });
  return buildActionButtonsUISpec(entries.filter(isNonNullable), 'chat');
}

function adaptProductList(event: V1ProductList): StreamEventUISpec {
  const groupedAssignment = Array.isArray(event.payload.grouped_assignment) ? event.payload.grouped_assignment : [];
  const rawStyleVariations = Array.isArray(event.payload.style_variations) ? event.payload.style_variations : [];
  const normalizedStyleVariations = rawStyleVariations
    .map((variation) => {
      if (!asRecord(variation)) return null;
      const styleLabel = firstNonEmptyString(variation.style_label);
      if (!styleLabel) return null;
      const styleMood = firstNonEmptyString(variation.style_mood);
      const imageUrl = firstNonEmptyString(variation.image_url ?? undefined);
      const productList = Array.isArray(variation.product_list) ? variation.product_list : [];
      const normalizedProducts = productList
        .map((entry) => {
          const entryRecord = asRecord(entry);
          if (!entryRecord) return null;
          const productRecord =
            asRecord(entryRecord['product_detail']) ?? asRecord(entryRecord['product']) ?? entryRecord;
          return productRecordToNormalized(productRecord) as unknown as Record<string, unknown> | null;
        })
        .filter(isNonNullable);
      const recommendationGroups = Array.isArray(variation.recommendation_groups)
        ? variation.recommendation_groups
            .map((group) => {
              if (!asRecord(group)) return null;
              return {
                label: firstNonEmptyString(group.label) ?? '',
                reason: firstNonEmptyString(group.reason) ?? '',
                skus: Array.isArray(group.skus)
                  ? group.skus.filter((sku): sku is string => typeof sku === 'string' && sku.trim().length > 0)
                  : [],
              };
            })
            .filter(isNonNullable)
        : [];

      return {
        style_label: styleLabel,
        style_mood: styleMood ?? '',
        ...(imageUrl ? { image_url: imageUrl } : {}),
        ...(typeof variation.status === 'string' ? { status: variation.status } : {}),
        product_list: normalizedProducts,
        recommendation_groups: recommendationGroups,
      };
    })
    .filter(isNonNullable);

  const fallbackProducts = event.payload.product_list ?? [];

  // For consulting sources, build grid children from the already-normalized
  // first variation so wrapper formats (product_detail, product) are properly
  // unwrapped.  Non-consulting backends always use the top-level product_list.
  const firstNormalized =
    isConsultingSource(event.payload.source) && normalizedStyleVariations.length > 0
      ? normalizedStyleVariations[0]!.product_list
      : null;

  const spec = firstNormalized
    ? buildProductGridFromNormalized(firstNormalized, 'chat')
    : groupedAssignment.length > 0
      ? (buildGroupedProductGridFromAssignment(groupedAssignment, fallbackProducts, 'chat') ??
        buildProductGridUISpec(fallbackProducts, 'chat'))
      : buildProductGridUISpec(fallbackProducts, 'chat');
  spec.panelHint = 'panel';
  // Pass pagination fields and backend-provided title
  const root = spec.spec.elements[spec.spec.root];
  if (root) {
    const extra: Record<string, unknown> = {};
    if (typeof event.payload.offset === 'number') extra['offset'] = event.payload.offset;
    if (typeof event.payload.end_of_list === 'boolean') extra['endOfList'] = event.payload.end_of_list;
    if (typeof event.payload.title === 'string') extra['panelTitle'] = event.payload.title;
    if (typeof event.payload.source === 'string') extra['source'] = event.payload.source;
    if (normalizedStyleVariations.length > 0) extra['styleVariations'] = normalizedStyleVariations;
    if (event.payload.replace_panel === true) extra['replacePanel'] = true;
    // search-revision: pass rankingState + sequenceId through so chat/index.ts
    // can force replace-vs-append semantics when the backend splits a single
    // search into pending + final emissions.
    if (event.payload.ranking_state === 'pending' || event.payload.ranking_state === 'final') {
      extra['rankingState'] = event.payload.ranking_state;
    }
    const seqId = event.payload.sequence_id ?? event.payload.search_id;
    if (typeof seqId === 'string' && seqId.length > 0) {
      extra['sequenceId'] = seqId;
    }
    if (Object.keys(extra).length > 0) root.props = { ...root.props, ...extra };
  }
  return spec;
}

function adaptProductDetails(event: V1ProductDetails): StreamEventUISpec {
  const product = event.payload.productDetails;
  if (!product) {
    return buildEmptyUISpec('chat');
  }
  const normalized = productToNormalized(product) as unknown as Record<string, unknown>;
  const detailProduct = {
    ...(product as unknown as Record<string, unknown>),
    ...normalized,
  };
  return {
    type: 'ui_spec',
    widget: 'chat',
    spec: {
      root: 'root',
      elements: {
        root: {
          type: 'ProductDetailsPanel',
          props: { product: detailProduct },
        },
      },
    },
    panelHint: 'panel',
  };
}

function adaptProductDetailsSimilars(event: V1ProductDetailsSimilars): StreamEventUISpec {
  const base = buildProductGridUISpec(event.payload.similarProducts ?? [], 'chat');
  // Mark for panel append rather than replace
  const root = base.spec.elements[base.spec.root];
  if (root) root.props = { ...root.props, similarsAppend: true };
  return { ...base, panelHint: 'panel' };
}

function adaptComparisonTable(event: V1ComparisonTable): StreamEventUISpec {
  const products = event.payload.multiple_product_details ?? [];
  const payloadRecord = event.payload as Record<string, unknown>;
  const framework = {
    key_differences: payloadRecord['key_differences'],
    recommended_choice: payloadRecord['recommended_choice'],
    recommended_choice_sku: payloadRecord['recommended_choice_sku'],
    special_considerations: payloadRecord['special_considerations'],
    criteria_view: payloadRecord['criteria_view'],
    criteria_view_short: payloadRecord['criteria_view_short'],
    compared_field_names: payloadRecord['compared_field_names'],
    winner_product: payloadRecord['winner_product'],
    winner_hits: payloadRecord['winner_hits'],
    ...(event.payload.product_comparison_framework ?? {}),
  } as NonNullable<V1ComparisonTable['payload']['product_comparison_framework']>;
  const table = event.payload.table;
  const featuresList = event.payload.features_list;

  // Normalize products
  const normalizedProducts: Array<Record<string, unknown>> = [];
  for (const p of products) {
    const norm = productToNormalized(p);
    normalizedProducts.push(norm as unknown as Record<string, unknown>);
  }

  const attributes = buildComparisonAttributes(table, normalizedProducts, framework, featuresList);

  // Find recommended product
  let recommendedSku: string | undefined;
  if (framework?.recommended_choice_sku) {
    recommendedSku = framework.recommended_choice_sku;
  } else if (framework?.winner_product && framework.winner_product.length > 0) {
    // Legacy structure wraps sku in product_detail; fall back to flat sku
    recommendedSku = framework.winner_product[0]?.product_detail?.sku ?? framework.winner_product[0]?.sku;
  }

  // Find recommended product object
  const recommended = recommendedSku
    ? (normalizedProducts.find((p) => p['sku'] === recommendedSku) ?? normalizedProducts[0])
    : normalizedProducts[0];

  // Extract highlights (key differences)
  const highlights: string[] = [];
  if (Array.isArray(framework?.key_differences)) {
    for (const diff of framework.key_differences) {
      if (typeof diff === 'string') highlights.push(diff);
    }
  }

  // Extract special cases
  const specialCases = normalizeStringList(framework?.special_considerations);

  // Build recommended choice explanation
  const recommendedText = framework?.recommended_choice;

  // Build winner hits per product
  const winnerHits = framework?.winner_hits;

  // Build product actions for "View Product" buttons
  const productActions: Record<string, Record<string, unknown>> = {};
  for (const p of normalizedProducts) {
    const sku = p['sku'] as string;
    if (sku) {
      productActions[sku] = {
        title: (p['name'] as string) ?? sku,
        type: 'launchSingleProduct',
        payload: { sku },
      };
    }
  }

  const props: Record<string, unknown> = {
    products: normalizedProducts,
    attributes,
    highlights,
    productActions,
  };

  if (recommended) props['recommended'] = recommended;
  if (specialCases.length > 0) props['specialCases'] = specialCases;
  if (recommendedText) props['recommendedText'] = recommendedText;
  if (winnerHits) props['winnerHits'] = winnerHits;

  // key_differences may be a single HTML string — pass raw for formatted rendering
  if (typeof framework?.key_differences === 'string') {
    props['keyDifferencesHtml'] = framework.key_differences;
  }

  // Pass special_considerations as structured data
  if (framework?.special_considerations) {
    props['specialConsiderations'] = framework.special_considerations;
  }

  return {
    type: 'ui_spec',
    widget: 'chat',
    spec: {
      root: 'root',
      elements: {
        root: {
          type: 'ComparisonTable',
          props,
        },
      },
    },
    panelHint: 'panel',
  };
}

function buildComparisonAttributes(
  table: V1ComparisonTable['payload']['table'],
  products: Array<Record<string, unknown>>,
  framework: NonNullable<V1ComparisonTable['payload']['product_comparison_framework']>,
  featuresList?: string[],
): Array<{ label: string; values: string[] }> {
  if (!table) return [];

  const entries = Object.entries(table);
  if (entries.length === 0) return [];

  const firstValue = entries[0]?.[1];
  if (Array.isArray(firstValue)) {
    const displayNames = asRecord(framework.criteria_view) ?? asRecord(framework.criteria_view_short) ?? {};
    const declaredOrder = (Array.isArray(framework.compared_field_names) ? framework.compared_field_names : []).filter(
      (f): f is string => typeof f === 'string',
    );
    const fieldOrder = declaredOrder.length > 0 ? declaredOrder : Object.keys(table);
    const attributes: Array<{ label: string; values: string[] }> = [];
    for (const fieldName of fieldOrder) {
      const values = table[fieldName];
      if (!values || !Array.isArray(values)) continue;
      const display = displayNames[fieldName];
      const label = typeof display === 'string' && display ? display : fieldName;
      attributes.push({ label, values: values.map((v) => (typeof v === 'string' ? v : String(v ?? ''))) });
    }
    return attributes;
  }

  const rowMap = table as Record<string, Record<string, unknown>>;
  const orderedSkus = products.map((product) => String(product['sku'] ?? '')).filter((sku) => sku.length > 0);
  const displayNames = asRecord(framework.criteria_view) ?? asRecord(framework.criteria_view_short) ?? {};
  const declaredFieldOrder = (
    Array.isArray(framework.compared_field_names) ? framework.compared_field_names : []
  ).filter((f): f is string => typeof f === 'string');
  const rawFieldOrder =
    featuresList && featuresList.length > 0
      ? featuresList
      : declaredFieldOrder.length > 0
        ? declaredFieldOrder
        : collectComparisonFields(rowMap);

  const fieldOrder = rawFieldOrder.filter(
    (field) => field !== 'name' && field !== 'name_short' && !field.endsWith('_short'),
  );
  const attributes: Array<{ label: string; values: string[] }> = [];

  for (const fieldName of fieldOrder) {
    const values = orderedSkus.map((sku) => {
      const row = rowMap[sku];
      if (!row || typeof row !== 'object') return '';
      const shortValue = row[`${fieldName}_short`];
      const longValue = row[fieldName];
      return stringifyComparisonValue(shortValue ?? longValue);
    });
    if (values.every((value) => value.length === 0)) continue;
    const display = displayNames[fieldName];
    const label = typeof display === 'string' && display ? display : fieldName;
    attributes.push({ label, values });
  }

  return attributes;
}

function collectComparisonFields(rowMap: Record<string, Record<string, unknown>>): string[] {
  const fields: string[] = [];
  const seen = new Set<string>();
  for (const row of Object.values(rowMap)) {
    if (!row || typeof row !== 'object') continue;
    for (const key of Object.keys(row)) {
      if (seen.has(key)) continue;
      seen.add(key);
      fields.push(key);
    }
  }
  return fields;
}

function stringifyComparisonValue(value: unknown): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return '';
}

function adaptContext(event: V1Context): StreamEventMetadata {
  return {
    type: 'metadata',
    sessionId: '',
    model: '',
    meta: {
      panel: event.payload.panel,
      messages: event.payload.messages,
      message_id: event.payload.message_id,
    },
  };
}

function adaptChatStreamEnd(): StreamEventDone {
  return { type: 'done' };
}

function adaptLoading(event: V1Loading): StreamEventMetadata {
  return {
    type: 'metadata',
    sessionId: '',
    model: '',
    meta: {
      loading: true,
      loadingText: event.payload.text,
      thinkingMessages: event.payload.thinking_messages,
      dynamicLoading: event.payload.is_dynamic === true,
    },
  };
}

function adaptPanelLoading(event: V1PanelLoading): StreamEventMetadata {
  return {
    type: 'metadata',
    sessionId: '',
    model: '',
    meta: {
      loading: true,
      panelLoading: true,
      panelPendingType: event.payload?.pending_type,
      loadingText: event.payload?.text,
    },
  };
}

function adaptSimilarLoading(event: V1SimilarLoading): StreamEventMetadata {
  return {
    type: 'metadata',
    sessionId: '',
    model: '',
    meta: {
      loading: true,
      similarPanelLoading: true,
      panelPendingType: event.payload?.pending_type,
      loadingText: event.payload?.text,
    },
  };
}

function adaptRedirect(event: V1Redirect): StreamEventAction | StreamEventMetadata {
  const url = firstNonEmptyString(event.payload.url);
  if (url) {
    return {
      type: 'action',
      action: {
        kind: 'navigate',
        url,
        newTab: event.payload.new_tab === true,
      },
    };
  }

  return {
    type: 'metadata',
    sessionId: '',
    model: '',
    meta: {
      redirect: event.payload,
      redirectTarget: firstNonEmptyString(event.payload.to),
    },
  };
}

function adaptV1Error(event: V1Error): StreamEventError {
  const message =
    firstNonEmptyString(event.payload?.text, event.payload?.message, event.payload?.error) ??
    'Backend returned an error';
  return {
    type: 'error',
    code: 'BACKEND_ERROR',
    message,
  };
}

function adaptNoop(_event: V1Noop): StreamEventMetadata {
  return {
    type: 'metadata',
    sessionId: '',
    model: '',
    meta: {
      noop: true,
    },
  };
}

function adaptLauncherAction(event: V1LauncherAction): StreamEvent {
  const payload = event.payload ?? {};

  const hasActionList = Array.isArray(payload['action_list']);
  if (hasActionList) {
    const quickQnaPayload: V1LauncherQuickQna['payload'] = {
      action_list: payload['action_list'] as V1SuggestedActionItem[],
    };
    const launcherType = typeof payload['type'] === 'string' ? payload['type'] : undefined;
    const theme = typeof payload['theme'] === 'string' ? payload['theme'] : undefined;
    if (launcherType !== undefined) quickQnaPayload.type = launcherType;
    if (theme !== undefined) quickQnaPayload.theme = theme;

    return adaptLauncherQuickQna({
      type: 'quick_qna',
      payload: quickQnaPayload,
    });
  }

  const action = asRecord(payload['action']);
  if (action) {
    const textImagePayload: V1LauncherTextImage['payload'] = {
      action,
    };
    const text = typeof payload['text'] === 'string' ? payload['text'] : undefined;
    const imageUrl = typeof payload['image_url'] === 'string' ? payload['image_url'] : undefined;
    const theme = typeof payload['theme'] === 'string' ? payload['theme'] : undefined;
    if (text !== undefined) textImagePayload.text = text;
    if (imageUrl !== undefined) textImagePayload.image_url = imageUrl;
    if (theme !== undefined) textImagePayload.theme = theme;

    return adaptLauncherTextImage({
      type: 'text_image',
      payload: textImagePayload,
    });
  }

  const text = typeof payload['text'] === 'string' ? payload['text'] : '';
  if (text) {
    const launcherTextPayload: V1LauncherText['payload'] = { text };
    const launcherType = typeof payload['type'] === 'string' ? payload['type'] : undefined;
    const theme = typeof payload['theme'] === 'string' ? payload['theme'] : undefined;
    const payloadObj = asRecord(payload['payload']) ?? undefined;
    if (launcherType !== undefined) launcherTextPayload.type = launcherType;
    if (payloadObj !== undefined) launcherTextPayload.payload = payloadObj;
    if (theme !== undefined) launcherTextPayload.theme = theme;

    return adaptLauncherText({
      type: 'text',
      payload: launcherTextPayload,
    });
  }

  return {
    type: 'metadata',
    sessionId: '',
    model: '',
    meta: {
      launcherAction: payload,
    },
  };
}

function adaptLauncherText(event: V1LauncherText): StreamEventUISpec {
  const props: Record<string, unknown> = {
    text: firstNonEmptyString(event.payload.text) ?? '',
  };
  if (typeof event.payload.theme === 'string') props['theme'] = event.payload.theme;
  if (event.payload.payload !== undefined) props['payload'] = event.payload.payload;

  return {
    type: 'ui_spec',
    widget: 'qna',
    spec: {
      root: 'root',
      elements: {
        root: {
          type: 'QuestionHeading',
          props,
        },
      },
    },
  };
}

function adaptProductItem(event: V1ProductItem): StreamEventUISpec {
  return buildSingleProductUISpec(event.payload, 'qna');
}

function adaptLauncherTextImage(event: V1LauncherTextImage): StreamEventUISpec {
  const label = firstNonEmptyString(event.payload.text) ?? '';
  const action = requestDetailsToAction(event.payload.action, label);
  if (action) {
    const props: Record<string, unknown> = {
      label: label || action.title,
      action,
    };
    if (typeof event.payload.image_url === 'string') props['image'] = event.payload.image_url;
    if (typeof event.payload.theme === 'string') props['theme'] = event.payload.theme;

    return {
      type: 'ui_spec',
      widget: 'qna',
      spec: {
        root: 'root',
        elements: {
          root: {
            type: 'ActionButton',
            props,
          },
        },
      },
    };
  }

  const fallbackPayload: V1LauncherText['payload'] = {
    text: label,
  };
  if (typeof event.payload.theme === 'string') fallbackPayload.theme = event.payload.theme;

  return adaptLauncherText({
    type: 'text',
    payload: fallbackPayload,
  });
}

function adaptLauncherQuickQna(event: V1LauncherQuickQna): StreamEventUISpec {
  const entries = (event.payload.action_list ?? []).map((action) => {
    if (!asRecord(action)) return null;
    const label = firstNonEmptyString(action.title) ?? '';
    const actionPayload = requestDetailsToAction(action.requestDetails, label);
    const result: ButtonEntry | null = actionPayload
      ? {
          label,
          action: actionPayload,
        }
      : null;
    if (!result) return null;
    if (typeof action.icon === 'string') result.icon = action.icon;
    if (typeof action.image === 'string') result.image = action.image;
    return result;
  });

  return buildActionButtonsUISpec(entries.filter(isNonNullable), 'qna');
}

function adaptReviewHighlights(event: V1ReviewHighlights): StreamEventUISpec {
  const reviews = (event.payload.reviews ?? [])
    .map((item) => {
      if (!asRecord(item)) return null;
      const review: Record<string, unknown> = {};
      if (typeof item.review_class === 'string') review['review_class'] = item.review_class;
      if (typeof item.review_text === 'string') review['review_text'] = item.review_text;
      if (typeof item.review_rating === 'string' || typeof item.review_rating === 'number') {
        review['review_rating'] = item.review_rating;
      }
      if (typeof item.review_tag === 'string') review['review_tag'] = item.review_tag;
      return review;
    })
    .filter(isNonNullable);

  const props: Record<string, unknown> = { reviews };
  if (typeof event.payload.sku === 'string') props['sku'] = event.payload.sku;

  return {
    type: 'ui_spec',
    widget: 'chat',
    spec: {
      root: 'root',
      elements: {
        root: {
          type: 'ReviewHighlights',
          props,
        },
      },
    },
  };
}

function adaptAiProductSuggestions(event: V1AiProductSuggestions): StreamEventUISpec | StreamEventMetadata {
  const suggestions = event.payload.product_suggestions ?? [];
  const items: Record<string, unknown>[] = [];

  for (let i = 0; i < suggestions.length; i++) {
    const suggestion = suggestions[i];
    if (!suggestion) continue;

    const product = suggestionToNormalizedProduct(suggestion);
    if (!product) continue;

    const item: Record<string, unknown> = { product };

    const action = requestDetailsToAction(suggestion.requestDetails, product.name);
    if (action) item['action'] = action;
    if (typeof suggestion.role === 'string') item['role'] = suggestion.role;
    if (typeof suggestion.reason === 'string') item['reason'] = suggestion.reason;
    if (typeof suggestion.review_highlight === 'string') item['reviewHighlight'] = suggestion.review_highlight;
    if (Array.isArray(suggestion.labels)) item['labels'] = suggestion.labels;
    if (typeof suggestion.expert_quality_score === 'number')
      item['expertQualityScore'] = suggestion.expert_quality_score;

    items.push(item);
  }

  if (items.length === 0) {
    return {
      type: 'metadata',
      sessionId: '',
      model: '',
      meta: {
        aiProductSuggestions: event.payload.product_suggestions ?? [],
      },
    };
  }

  return {
    type: 'ui_spec',
    widget: 'chat',
    spec: {
      root: 'root',
      elements: {
        root: {
          type: 'AITopPicks',
          props: { suggestions: items },
        },
      },
    },
  };
}

function adaptAiProductGroupings(event: V1AiProductGroupings): StreamEventUISpec | StreamEventMetadata {
  const payloadGroupings = event.payload.product_groupings ?? [];
  const groups: Array<{
    groupName: string;
    image?: string;
    description?: string;
    highlight?: string;
    labels?: string[];
    products: Record<string, unknown>[];
  }> = [];
  const entries: Array<Record<string, unknown>> = [];

  for (let i = 0; i < payloadGroupings.length; i++) {
    const grouping = payloadGroupings[i];
    if (!grouping) continue;
    const label = firstNonEmptyString(grouping.name) ?? '';
    const groupDescription = firstNonEmptyString(grouping.description);
    const groupHighlight = firstNonEmptyString(grouping.highlight);
    const groupLabels = Array.isArray(grouping.labels) ? normalizeStringList(grouping.labels) : [];
    const representativeSku = firstNonEmptyString(grouping.sku, grouping.repr_sku);
    const representativeImage = firstNonEmptyString(grouping.image, grouping.repr_image);
    const action = aiProductGroupingAction(grouping, label, representativeSku);

    const rawProducts = Array.isArray(grouping.group_products) ? grouping.group_products : [];
    const normalizedProducts = rawProducts
      .map((product) => {
        const record = asRecord(product);
        return record ? (productRecordToNormalized(record) as unknown as Record<string, unknown>) : null;
      })
      .filter(isNonNullable);
    const groupImage =
      representativeImage ?? firstNonEmptyString(...normalizedProducts.map((product) => product['imageUrl']));
    if (label && normalizedProducts.length > 0) {
      const group: (typeof groups)[number] = { groupName: label, products: normalizedProducts };
      if (groupImage) group.image = groupImage;
      if (groupDescription) group.description = groupDescription;
      if (groupHighlight) group.highlight = groupHighlight;
      if (groupLabels.length > 0) group.labels = groupLabels;
      groups.push(group);
    }

    if (!action) continue;

    const entry: Record<string, unknown> = { name: label, action };
    if (groupLabels.length > 0) entry['labels'] = groupLabels;
    if (representativeImage !== undefined) entry['image'] = representativeImage;
    entries.push(entry);
  }

  // Product-backed groups are panel-native content. Mixed payloads should be
  // avoided by the backend; if they occur, product-backed groups take precedence
  // over action-card entries for this event.
  if (groups.length > 0) {
    return {
      type: 'ui_spec',
      widget: 'chat',
      spec: {
        root: 'root',
        elements: {
          root: {
            type: 'CategoriesContainer',
            props: {
              groups,
            },
          },
        },
      },
      panelHint: 'panel',
    };
  }

  if (entries.length === 0) {
    return {
      type: 'metadata',
      sessionId: '',
      model: '',
      meta: {
        aiProductGroupings: event.payload.product_groupings ?? [],
      },
    };
  }

  return {
    type: 'ui_spec',
    widget: 'chat',
    spec: {
      root: 'root',
      elements: {
        root: {
          type: 'AIGroupingCards',
          props: { entries },
        },
      },
    },
  };
}

function aiProductGroupingAction(
  grouping: V1AiProductGrouping,
  label: string,
  representativeSku: string | undefined,
): { title: string; type: string; payload?: unknown } | null {
  const details = asRecord(grouping.requestDetails);
  if (details) {
    const type = details['type'];
    if (type !== 'findSimilar') return requestDetailsToAction(details, label);

    const payload = asRecord(details['payload']);
    const mergedPayload: Record<string, unknown> = { ...(payload ?? {}) };
    if (representativeSku && !firstNonEmptyStringValue(mergedPayload['sku'])) {
      mergedPayload['sku'] = representativeSku;
    }

    const groupingSkus = nonEmptyStringList(grouping.group_skus);
    if (groupingSkus.length > 0) {
      mergedPayload['group_skus'] = groupingSkus;
    }

    return requestDetailsToAction(
      {
        ...details,
        payload: normalizeFindSimilarPayload(mergedPayload),
      },
      label,
    );
  }

  const fallbackPayload: Record<string, unknown> = {};
  if (representativeSku) fallbackPayload['sku'] = representativeSku;
  const groupingSkus = nonEmptyStringList(grouping.group_skus);
  if (groupingSkus.length > 0) fallbackPayload['group_skus'] = groupingSkus;
  if (Object.keys(fallbackPayload).length === 0) return null;

  return requestDetailsToAction(
    {
      type: 'findSimilar',
      payload: normalizeFindSimilarPayload(fallbackPayload),
    },
    label,
  );
}

function aiSuggestedSearchAction(
  search: V1AiSuggestedSearch,
  label: string,
): { title: string; type: string; payload?: unknown } | null {
  const requestDetailsRecord = asRecord(search.requestDetails);
  const requestPayload = asRecord(requestDetailsRecord?.['payload']);
  const detailedText = firstNonEmptyString(search.detailed_user_message);
  const groupingSkus =
    search.group_skus && Array.isArray(search.group_skus)
      ? nonEmptyStringList(search.group_skus)
      : nonEmptyStringList(requestPayload?.['group_skus']);
  const representativeSku = firstNonEmptyString(search.sku, search.representative_product_sku, requestPayload?.['sku']);

  if (requestDetailsRecord?.['type'] === 'findSimilar') {
    const mergedPayload: Record<string, unknown> = { ...(requestPayload ?? {}) };
    if (representativeSku) mergedPayload['sku'] = representativeSku;
    if (groupingSkus.length > 0) mergedPayload['group_skus'] = groupingSkus;
    if (detailedText) {
      mergedPayload['input'] = detailedText;
      if (!firstNonEmptyStringValue(mergedPayload['text'])) mergedPayload['text'] = detailedText;
    }
    mergedPayload['is_search_suggestion'] = 1;
    return requestDetailsToAction(
      {
        ...requestDetailsRecord,
        payload: normalizeFindSimilarPayload(mergedPayload),
      },
      label,
    );
  }

  if (representativeSku) {
    const mergedPayload: Record<string, unknown> = { sku: representativeSku, is_search_suggestion: 1 };
    if (groupingSkus.length > 0) mergedPayload['group_skus'] = groupingSkus;
    if (detailedText) {
      mergedPayload['input'] = detailedText;
      mergedPayload['text'] = detailedText;
    }
    return requestDetailsToAction(
      {
        type: 'findSimilar',
        payload: normalizeFindSimilarPayload(mergedPayload),
      },
      label,
    );
  }

  const fallbackPayload: Record<string, unknown> = {
    ...(requestPayload ?? {}),
    is_suggested_text: 1,
    is_search_suggestion: 1,
  };
  if (detailedText && !firstNonEmptyStringValue(fallbackPayload['text'])) fallbackPayload['text'] = detailedText;
  if (groupingSkus.length > 0) fallbackPayload['group_skus'] = groupingSkus;
  const fallbackRequest: V1RequestDetails =
    requestDetailsRecord?.['type'] === 'inputText'
      ? { ...requestDetailsRecord, payload: fallbackPayload }
      : { type: 'inputText', payload: fallbackPayload };
  return requestDetailsToAction(fallbackRequest, label);
}

function adaptAiSuggestedSearches(event: V1AiSuggestedSearches): StreamEventUISpec | StreamEventMetadata {
  const searches = event.payload.suggested_searches ?? [];
  const entries: Array<Record<string, unknown>> = [];

  for (let i = 0; i < searches.length; i++) {
    const search = searches[i];
    if (!search) continue;

    const shortName =
      firstNonEmptyString(search.short_name, search.chosen_attribute, search.detailed_user_message) ??
      `Search ${i + 1}`;
    const action = aiSuggestedSearchAction(search, shortName);
    if (!action) continue;

    const entry: Record<string, unknown> = { shortName, action };
    const detailedMessage = firstNonEmptyString(search.detailed_user_message);
    if (detailedMessage && detailedMessage !== shortName) entry['detailedMessage'] = detailedMessage;
    const keywordLine = getSuggestedSearchKeywordsText(search);
    if (keywordLine) {
      const dm = detailedMessage ?? '';
      if (keywordLine !== shortName && keywordLine !== dm) {
        entry['whyDifferent'] = keywordLine;
      }
    }
    if (typeof search.image === 'string') entry['image'] = search.image;
    entries.push(entry);
  }

  if (entries.length === 0) {
    return {
      type: 'metadata',
      sessionId: '',
      model: '',
      meta: {
        aiSuggestedSearches: event.payload.suggested_searches ?? [],
      },
    };
  }

  return {
    type: 'ui_spec',
    widget: 'chat',
    spec: {
      root: 'root',
      elements: {
        root: {
          type: 'AISuggestedSearchCards',
          props: { entries },
        },
      },
    },
  };
}

function adaptGetGroundingReview(event: V1GetGroundingReview): StreamEventUISpec | StreamEventMetadata {
  const p = event.payload;
  const requestDetails = p.requestDetails ?? p['request_details'];
  const action = requestDetailsToAction(
    requestDetails,
    firstNonEmptyString(p.review_count, p['reviewCount'], p.text, p.title) ?? 'Show product reviews',
  );
  if (!action) {
    return {
      type: 'metadata',
      sessionId: '',
      model: '',
      meta: {
        groundingReview: p,
      },
    };
  }

  const props: Record<string, unknown> = { action };
  if (p.title) props['title'] = p.title;
  if (p.text) props['text'] = p.text;
  if (p.review_count) props['reviewCount'] = p.review_count;
  else if (typeof p['reviewCount'] === 'string' && p['reviewCount'].trim()) props['reviewCount'] = p['reviewCount'];

  return {
    type: 'ui_spec',
    widget: 'chat',
    spec: {
      root: 'root',
      elements: {
        root: {
          type: 'GroundingReviewCard',
          props,
        },
      },
    },
  };
}

function adaptVoice(event: V1Voice): StreamEventMetadata {
  return {
    type: 'metadata',
    sessionId: '',
    model: '',
    meta: {
      voice: event.payload,
    },
  };
}

interface ButtonEntry {
  label: string;
  action: { title: string; type: string; payload?: unknown };
  icon?: string;
  image?: string;
  description?: string;
}

function buildProductCardElement(product: NormalizedProduct | Record<string, unknown>, index: number): UIElement {
  const props: Record<string, unknown> = { product, index };
  const productRecord = product as Record<string, unknown>;
  const sku = typeof productRecord['sku'] === 'string' ? productRecord['sku'] : undefined;
  if (sku) {
    props['action'] = {
      title: typeof productRecord['name'] === 'string' ? productRecord['name'] : '',
      type: 'launchSingleProduct',
      payload: { sku },
    };
  }
  return { type: 'ProductCard', props };
}

function buildActionButtonsUISpec(entries: ButtonEntry[], widget: WidgetName): StreamEventUISpec {
  const elements: Record<string, UIElement> = {};
  const childIds: string[] = [];

  for (let i = 0; i < entries.length; i++) {
    const entry = entries[i];
    if (!entry) continue;
    const id = `action-${i}`;
    childIds.push(id);
    const props: Record<string, unknown> = {
      label: entry.label,
      action: entry.action,
    };
    if (entry.icon !== undefined) props['icon'] = entry.icon;
    if (entry.image !== undefined) props['image'] = entry.image;
    if (entry.description !== undefined) props['description'] = entry.description;

    elements[id] = {
      type: 'ActionButton',
      props,
    };
  }

  elements['root'] = {
    type: 'ActionButtons',
    props: {
      buttons: entries.map((entry) => {
        const btn: Record<string, unknown> = {
          label: entry.label,
          action: entry.action,
        };
        if (entry.image !== undefined) btn['image'] = entry.image;
        if (entry.description !== undefined) btn['description'] = entry.description;
        if (entry.icon !== undefined) btn['icon'] = entry.icon;
        return btn;
      }),
    },
    children: childIds,
  };

  return {
    type: 'ui_spec',
    widget,
    spec: { root: 'root', elements },
  };
}

function buildProductGridUISpec(products: V1Product[], widget: WidgetName): StreamEventUISpec {
  const elements: Record<string, UIElement> = {};
  const childIds: string[] = [];

  for (let i = 0; i < products.length; i++) {
    const product = products[i];
    if (!product) continue;
    const normalized = productToNormalized(product);
    const id = `product-${i}`;
    childIds.push(id);
    elements[id] = buildProductCardElement(normalized, i);
  }

  elements['root'] = {
    type: 'ProductGrid',
    props: { layout: 'grid' },
    children: childIds,
  };

  return {
    type: 'ui_spec',
    widget,
    spec: { root: 'root', elements },
  };
}

function buildGroupedProductGridFromAssignment(
  assignments: Array<{ title?: string; skus?: string[] }>,
  products: V1Product[],
  widget: WidgetName,
): StreamEventUISpec | null {
  const productsBySku = new Map<string, V1Product>();
  for (const product of products) {
    if (!product?.sku) continue;
    productsBySku.set(product.sku, product);
  }

  if (productsBySku.size === 0) return null;

  const assignedSkus = new Set<string>();
  const elements: Record<string, UIElement> = {};
  const childIds: string[] = [];
  const productSections: Array<{ title: string; children: string[] }> = [];
  let globalIndex = 0;

  for (let sectionIndex = 0; sectionIndex < assignments.length; sectionIndex++) {
    const assignment = assignments[sectionIndex];
    const title = firstNonEmptyString(assignment?.title) ?? '';
    const skus = Array.isArray(assignment?.skus) ? assignment.skus : [];
    const sectionChildIds: string[] = [];

    for (let skuIndex = 0; skuIndex < skus.length; skuIndex++) {
      const sku = typeof skus[skuIndex] === 'string' ? skus[skuIndex]!.trim() : '';
      if (!sku) continue;
      if (assignedSkus.has(sku)) continue;
      const product = productsBySku.get(sku);
      if (!product) continue;

      assignedSkus.add(sku);
      const id = `product-${sectionIndex}-${skuIndex}`;
      const normalized = productToNormalized(product);
      childIds.push(id);
      sectionChildIds.push(id);
      elements[id] = buildProductCardElement(normalized, globalIndex);
      globalIndex += 1;
    }

    if (sectionChildIds.length > 0) {
      productSections.push({ title, children: sectionChildIds });
    }
  }

  if (productSections.length === 0) return null;
  if (assignedSkus.size < productsBySku.size) {
    if (import.meta.env?.DEV) {
      const missing = Array.from(productsBySku.keys()).filter((sku) => !assignedSkus.has(sku));
      console.warn('[gengage:protocol] grouped_assignment missing SKUs, falling back to flat grid:', missing);
    }
    return null;
  }

  elements['root'] = {
    type: 'ProductGrid',
    props: { layout: 'grouped', productSections },
    children: childIds,
  };

  return {
    type: 'ui_spec',
    widget,
    spec: { root: 'root', elements },
  };
}

/**
 * Build a ProductGrid UISpec from already-normalized product records.
 * Used for consulting grids where products were normalized via
 * productRecordToNormalized (which unwraps product_detail wrappers).
 */
function buildProductGridFromNormalized(products: Record<string, unknown>[], widget: WidgetName): StreamEventUISpec {
  const elements: Record<string, UIElement> = {};
  const childIds: string[] = [];

  for (let i = 0; i < products.length; i++) {
    const product = products[i];
    if (!product) continue;
    const id = `product-${i}`;
    childIds.push(id);
    elements[id] = buildProductCardElement(product, i);
  }

  elements['root'] = {
    type: 'ProductGrid',
    props: { layout: 'grid' },
    children: childIds,
  };

  return {
    type: 'ui_spec',
    widget,
    spec: { root: 'root', elements },
  };
}

function buildSingleProductUISpec(product: V1Product, widget: WidgetName): StreamEventUISpec {
  return {
    type: 'ui_spec',
    widget,
    spec: {
      root: 'root',
      elements: {
        root: {
          type: 'ProductCard',
          props: { product: productToNormalized(product), index: 0 },
        },
      },
    },
  };
}

function buildEmptyUISpec(widget: WidgetName): StreamEventUISpec {
  return {
    type: 'ui_spec',
    widget,
    spec: {
      root: 'root',
      elements: {
        root: {
          type: 'ActionButtons',
          props: { buttons: [] },
          children: [],
        },
      },
    },
  };
}

function suggestionToNormalizedProduct(suggestion: V1AiProductSuggestion): NormalizedProduct | null {
  const fallbackSku = firstNonEmptyString(suggestion.sku);
  const fallbackName = firstNonEmptyString(suggestion.short_name);
  const inner = asRecord(suggestion.product_item);
  const base = inner ?? (suggestion as Record<string, unknown>);
  const merged: Record<string, unknown> = { ...base };
  const fromSuggestionRoot = firstNonEmptyString(suggestion.discount_reason, suggestion['discountReason']);
  if (
    fromSuggestionRoot &&
    !firstNonEmptyString(merged['discount_reason'] as unknown, merged['discountReason'] as unknown)
  ) {
    merged['discount_reason'] = fromSuggestionRoot;
  }
  return productRecordToNormalized(merged, fallbackSku, fallbackName);
}

function productRecordToNormalized(
  raw: Record<string, unknown>,
  fallbackSku?: string,
  fallbackName?: string,
): NormalizedProduct | null {
  const sku = firstNonEmptyString(raw['sku'], fallbackSku);
  const name = firstNonEmptyString(raw['name'], fallbackName);
  if (!sku || !name) return null;

  const product: V1Product = {
    sku,
    name,
  };

  const brand = firstNonEmptyString(raw['brand']);
  if (brand) product.brand = brand;

  const url = firstNonEmptyString(raw['url']);
  if (url) product.url = url;

  const images = stringArray(raw['images']);
  if (images.length > 0) {
    product.images = images;
  } else {
    const image = firstNonEmptyString(raw['image'], raw['image_url'], raw['imageUrl']);
    if (image) product.images = [image];
  }

  const priceDiscounted = toNumber(raw['price_discounted']);
  if (priceDiscounted !== undefined) product.price_discounted = priceDiscounted;
  const price = toNumber(raw['price']);
  if (price !== undefined) product.price = price;
  const rating = toNumber(raw['rating']);
  if (rating !== undefined) product.rating = rating;
  const reviewCount = toNumber(raw['review_count']) ?? toNumber(raw['reviewCount']);
  if (reviewCount !== undefined) product.review_count = reviewCount;
  const cartCode = firstNonEmptyString(raw['cart_code'], raw['cartCode']);
  if (cartCode) product.cart_code = cartCode;
  if (typeof raw['in_stock'] === 'boolean') product.in_stock = raw['in_stock'];
  if (typeof raw['inStock'] === 'boolean') product.in_stock = raw['inStock'];

  const discountReason = firstNonEmptyString(raw['discount_reason'], raw['discountReason']);
  if (discountReason) product.discount_reason = discountReason;

  return productToNormalized(product);
}

function requestDetailsToAction(
  requestDetails: unknown,
  label: string,
): { title: string; type: string; payload?: unknown } | null {
  const details = asRecord(requestDetails);
  if (!details) return null;

  const type = details['type'];
  if (typeof type !== 'string' || type.length === 0) return null;

  const action: { title: string; type: string; payload?: unknown } = {
    title: label || type,
    type,
  };
  if (details['payload'] !== undefined) action.payload = details['payload'];
  return action;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function firstNonEmptyString(...values: unknown[]): string | undefined {
  for (const value of values) {
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed.length > 0) return trimmed;
    }
  }
  return undefined;
}

function stringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is string => typeof item === 'string' && item.length > 0);
}

function normalizeStringList(value: unknown): string[] {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed ? [trimmed] : [];
  }
  if (!Array.isArray(value)) return [];
  return value
    .filter((item): item is string => typeof item === 'string')
    .map((item) => item.trim())
    .filter((item) => item.length > 0);
}

function toNumber(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    // Blank/whitespace-only strings must not coerce to 0.
    if (!value.trim()) return undefined;
    // Handle Turkish number format: "1.299,99" → "1299.99". The parse assumes
    // Turkish formatting ('.' = thousands sep, ',' = decimal); backend emits
    // numeric floats today, so this is defensive.
    // If the string contains both '.' and ',', treat '.' as thousands separator and ',' as decimal
    let normalized: string;
    if (value.includes('.') && value.includes(',')) {
      normalized = value.replace(/\./g, '').replace(',', '.');
    } else {
      normalized = value.replace(',', '.');
    }
    const parsed = Number(normalized);
    if (Number.isFinite(parsed)) return parsed;
  }
  return undefined;
}

function isNonNullable<T>(value: T | null | undefined): value is T {
  return value !== null && value !== undefined;
}

export interface NormalizedProduct {
  sku: string;
  name: string;
  imageUrl?: string;
  images?: string[];
  price?: string;
  originalPrice?: string;
  discountPercent?: number;
  url: string;
  brand?: string;
  rating?: number;
  reviewCount?: number;
  cartCode?: string;
  inStock?: boolean;
  variants?: Array<Record<string, unknown>>;
  discountReason?: string;
  promotions?: string[];
  description?: string;
  descriptionHtml?: string;
  features?: Array<{ name?: string; key?: string; value?: string | number | boolean; [key: string]: unknown }>;
  specifications?: Record<string, string> | Array<{ key: string; value: string }>;
  facetHits?: Record<string, unknown>;
  categoryNames?: string[];
  shortName?: string;
  /** Pass-through bag for backend fields not consumed by the SDK. */
  extras?: Record<string, unknown>;
}

/** V1 product keys consumed by productToNormalized — hoisted to avoid per-call Set allocation. */
const KNOWN_V1_PRODUCT_KEYS: ReadonlySet<string> = new Set([
  'sku',
  'name',
  'brand',
  'images',
  'price',
  'price_discounted',
  'price_discount_rate',
  'price_currency',
  'discount_reason',
  'url',
  'rating',
  'review_count',
  'cart_code',
  'in_stock',
  'description',
  'description_html',
  'features',
  'specifications',
  'short_name',
  'category_ids',
  'category_names',
  'variants',
  'facet_hits',
  'promotions',
]);

export function productToNormalized(p: V1Product): NormalizedProduct {
  const hasDiscount = p.price_discounted != null && p.price_discounted > 0;
  const price = hasDiscount ? p.price_discounted : p.price;
  const originalPrice = hasDiscount && p.price != null ? p.price : undefined;

  let discountPercent: number | undefined;
  if (originalPrice != null && price != null && originalPrice > 0) {
    discountPercent = Math.round(((originalPrice - price) / originalPrice) * 100);
  } else if (p.price_discount_rate != null && p.price_discount_rate > 0) {
    discountPercent = p.price_discount_rate;
  }

  const brand = firstNonEmptyString(p.brand);
  const name = firstNonEmptyString(p.name) ?? p.sku;
  const normalizedName = brand && !name.toLowerCase().startsWith(brand.toLowerCase()) ? `${brand} ${name}` : name;

  const result: NormalizedProduct = {
    sku: p.sku,
    name: normalizedName,
    url: firstNonEmptyString(p.url) ?? '',
  };

  const image = p.images?.[0];
  if (image) result.imageUrl = image;
  if (p.images && p.images.length > 1) result.images = p.images;
  if (price != null && price > 0) result.price = String(price);
  // Clamp out non-positive discounts. The faceted-search path skips price
  // normalization, so an inverted (price_discounted > price) discount can arrive.
  // When that happens we drop BOTH the badge and the strikethrough originalPrice
  // — otherwise the card would render a struck-through price lower than the
  // displayed one. originalPrice is only meaningful alongside a real discount.
  if (discountPercent !== undefined && discountPercent > 0) {
    result.discountPercent = discountPercent;
    if (originalPrice != null && originalPrice > 0) {
      result.originalPrice = String(originalPrice);
    }
  }
  if (brand !== undefined) result.brand = brand;
  if (p.rating !== undefined) result.rating = p.rating;
  if (p.review_count !== undefined) result.reviewCount = p.review_count;
  if (p.cart_code !== undefined) result.cartCode = p.cart_code;
  if (p.in_stock !== undefined) result.inStock = p.in_stock;
  if (p.variants && p.variants.length > 0) result.variants = p.variants;
  if (p.discount_reason !== undefined) result.discountReason = p.discount_reason;
  if (p.promotions && p.promotions.length > 0) result.promotions = p.promotions;
  if (p.description !== undefined) result.description = p.description;
  if (p.description_html !== undefined) result.descriptionHtml = p.description_html;
  if (p.features && p.features.length > 0) result.features = p.features;
  if (p.specifications !== undefined) result.specifications = p.specifications;
  if (p.facet_hits) result.facetHits = p.facet_hits;
  if (p.category_names && p.category_names.length > 0) result.categoryNames = p.category_names;
  if (p.short_name !== undefined) result.shortName = p.short_name;

  // Collect any extra backend fields not consumed above.
  const raw = p as unknown as Record<string, unknown>;
  const extras: Record<string, unknown> = {};
  let hasExtras = false;
  for (const key of Object.keys(raw)) {
    if (!KNOWN_V1_PRODUCT_KEYS.has(key)) {
      extras[key] = raw[key];
      hasExtras = true;
    }
  }
  if (hasExtras) result.extras = extras;

  return result;
}

export interface SimilarProductsJsonResponse {
  results: V1Product[];
  count: number;
  source_sku?: string;
}

export interface ProductGroupingsJsonResponse {
  intro_message?: string;
  product_groupings: Array<{
    name: string;
    description?: string;
    highlight?: string;
    repr_sku?: string;
    repr_image?: string;
    group_skus?: string[];
    group_products?: V1Product[];
  }>;
  count: number;
}

export function normalizeSimilarProductsResponse(json: SimilarProductsJsonResponse): NormalizedProduct[] {
  const results = Array.isArray(json?.results) ? json.results : [];
  // Guard each element too: a null entry in an otherwise-valid array would crash
  // productToNormalized (parity with the M4 streaming-adapter element guards).
  return results.filter((p) => asRecord(p) !== null).map(productToNormalized);
}

export function normalizeProductGroupingsResponse(json: ProductGroupingsJsonResponse): Array<{
  name: string;
  highlight?: string;
  products: NormalizedProduct[];
}> {
  const groupings = Array.isArray(json?.product_groupings) ? json.product_groupings : [];
  return groupings
    .filter((group) => asRecord(group) !== null)
    .map((group) => {
      const products = Array.isArray(group.group_products) ? group.group_products : [];
      const result: { name: string; highlight?: string; products: NormalizedProduct[] } = {
        name: group.name,
        products: products.filter((p) => asRecord(p) !== null).map(productToNormalized),
      };
      if (group.highlight !== undefined) result.highlight = group.highlight;
      return result;
    });
}

/**
 * Adapts a generic `uiSpec` backend event to a normalized `ui_spec` StreamEvent.
 *
 * The backend emits `{ type: "uiSpec", payload: { type: "BeautyPhotoStep", ... } }`.
 * This wraps the flat payload into the standard `{ root, elements }` UISpec shape
 * expected by the frontend renderer.
 */
function adaptUiSpec(event: V1UiSpec): StreamEventUISpec {
  const payload = asRecord(event.payload) ?? {};
  const componentType = typeof payload['type'] === 'string' ? payload['type'] : '';
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- destructure to exclude `type` from rest
  const { type: _type, ...rest } = payload;
  return {
    type: 'ui_spec',
    widget: 'chat',
    spec: {
      root: 'root',
      elements: {
        root: {
          type: componentType,
          props: rest,
        },
      },
    },
  };
}
